import { NgStyle } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, NgZone, viewChild } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MarkdownModule } from 'ngx-markdown';
import type { TailwindColor } from '../super-latex.model';

@Component({
  selector: 'dojo-menu-label-overlay',
  standalone: true,
  imports: [MatMenuModule, MarkdownModule, NgStyle],
  template: `
    <button
      #triggerButton
      class="menu-trigger"
      [ngStyle]="buttonStyles()"
      [matMenuTriggerFor]="menu"
      type="button"
      aria-label="Show info"
      (mousedown)="$event.stopPropagation(); $event.preventDefault()"
      (touchstart)="$event.stopPropagation()"
      (pointerdown)="$event.stopPropagation()"
    >
      <span class="pulse-dot" [ngStyle]="dotStyles()"></span>
    </button>

    <mat-menu #menu="matMenu" xPosition="after" yPosition="below">
      <div class="menu-content" [ngStyle]="menuContentStyles()">
        <markdown [data]="text()" katex [katexOptions]="katexOptions" />
      </div>
    </mat-menu>
  `,
  styles: `
    :host {
      display: inline-block;
    }

    .menu-trigger {
      position: relative;
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
      border-width: 2px;
      border-style: solid;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;

      &:focus {
        outline: none;
      }
    }

    .pulse-dot {
      position: absolute;
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 50%;
      transform: scale(1);
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(from currentColor r g b / 0.5);
      }

      70% {
        transform: scale(1);
        box-shadow: 0 0 0 6px rgba(from currentColor r g b / 0);
      }

      100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(from currentColor r g b / 0);
      }
    }

    ::ng-deep .mat-mdc-menu-panel {
      max-width: none !important;
    }

    ::ng-deep .mat-mdc-menu-content {
      padding: 0 !important;
    }

    .menu-content {
      padding: 0.75rem 1rem;
      max-width: 20rem;
      font-size: 0.875rem;
      line-height: 1.5;
      pointer-events: auto;
    }
  `,
})
export class MenuLabelOverlayComponent {
  constructor() {
    effect((onCleanup) => {
      const button = this.triggerButton().nativeElement;

      const preventPropagation = (e: Event) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };

      const clickHandler = (e: Event) => {
        preventPropagation(e);
        e.preventDefault();
        this.zone.run(() => this.trigger().toggleMenu());
      };

      // Stop mousedown/pointerdown/touchstart to prevent jsPlumb from interfering
      const downHandler = (e: Event) => {
        preventPropagation(e);
      };

      button.addEventListener('click', clickHandler, { capture: true });
      button.addEventListener('mousedown', downHandler, { capture: true });
      button.addEventListener('touchstart', downHandler, { capture: true });
      button.addEventListener('pointerdown', downHandler, { capture: true });

      onCleanup(() => {
        button.removeEventListener('click', clickHandler, { capture: true });
        button.removeEventListener('mousedown', downHandler, { capture: true });
        button.removeEventListener('touchstart', downHandler, { capture: true });
        button.removeEventListener('pointerdown', downHandler, { capture: true });
      });
    });
  }

  readonly text = input.required<string>();
  readonly color = input<TailwindColor>('sky');
  readonly offset = input<{ x?: number; y?: number }>();

  private readonly zone = inject(NgZone);
  readonly triggerButton = viewChild.required<ElementRef<HTMLElement>>('triggerButton');
  readonly trigger = viewChild.required(MatMenuTrigger);

  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
    trust: true,
  };

  readonly buttonStyles = computed(() => {
    const color = this.color();
    const offset = this.offset();
    const styles: Record<string, string> = {
      backgroundColor: `var(--${color}-50)`,
      borderColor: `var(--${color}-500)`,
    };

    if (offset?.x || offset?.y) {
      styles['transform'] = `translate(${offset.x ?? 0}px, ${offset.y ?? 0}px)`;
    }

    return styles;
  });

  readonly dotStyles = computed(() => {
    const color = this.color();
    return {
      backgroundColor: `var(--${color}-500)`,
    };
  });

  readonly menuContentStyles = computed(() => {
    const color = this.color();
    return {
      backgroundColor: `var(--${color}-50)`,
      color: `var(--${color}-700)`,
    };
  });
}
